import { Injectable, inject } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { Observable, filter, map, take } from 'rxjs';

export interface RecentVenueEntry {
  venueId: string;
  name: string;
  imagePath: string;
  enabled: boolean;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class RecentVenuesService {
  private auth = inject(Auth);

  private readonly STORAGE_PREFIX = 'blazebite_recent_venues_';
  private readonly MAX_ENTRIES = 8;
  private readonly MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
  private userId: string | null = null;
  private authReady = false;

  readonly recentVenues$: Observable<RecentVenueEntry[]>;

  constructor() {
    const authState$ = authState(this.auth);

    authState$.subscribe(user => {
      this.userId = user?.uid ?? null;
      this.authReady = true;
    });

    this.recentVenues$ = authState$.pipe(
      take(1),
      map(() => this.getRecentVenues())
    );
  }

  private getStorageKey(): string | null {
    if (!this.userId) return null;
    return `${this.STORAGE_PREFIX}${this.userId}`;
  }

  private loadEntries(): RecentVenueEntry[] {
    const key = this.getStorageKey();
    if (!key) return [];
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      return JSON.parse(raw) as RecentVenueEntry[];
    } catch {
      return [];
    }
  }

  private saveEntries(entries: RecentVenueEntry[]): void {
    const key = this.getStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(entries));
  }

  private purgeOldEntries(entries: RecentVenueEntry[]): RecentVenueEntry[] {
    const cutoff = Date.now() - this.MAX_AGE_MS;
    return entries.filter(e => e.timestamp >= cutoff);
  }

  trackVisit(venueId: string, name: string, imagePath: string, enabled: boolean): void {
    let entries = this.loadEntries();
    // Remove existing entry for this venue (will re-add at top)
    entries = entries.filter(e => e.venueId !== venueId);
    // Add new entry at the beginning
    entries.unshift({ venueId, name, imagePath, enabled, timestamp: Date.now() });
    // Purge old and trim to max
    entries = this.purgeOldEntries(entries).slice(0, this.MAX_ENTRIES);
    this.saveEntries(entries);
  }

  removeVenue(venueId: string): void {
    this.saveEntries(this.loadEntries().filter(e => e.venueId !== venueId));
  }

  getRecentVenues(): RecentVenueEntry[] {
    let entries = this.loadEntries();
    entries = this.purgeOldEntries(entries).slice(0, this.MAX_ENTRIES);
    // Save back the cleaned list
    this.saveEntries(entries);
    return entries;
  }
}
